import React from "react";
import { Link } from "react-router-dom";
import { Download, Printer, ArrowLeft, CalendarCheck } from "lucide-react";
import { EVIDENCE_WATERMARK, shortTime } from "../../demo/demoData";
import { useDemo } from "../../demo/DemoContext";
import { Button } from "../ui/button";

export default function EvidenceRecord({ incidentId, onBack }) {
  const { getIncident, buildTimeline } = useDemo();
  const incident = incidentId ? getIncident(incidentId) : null;

  if (!incident) {
    return (
      <div className="rounded-lg border border-white/10 bg-navy-900/60 p-8 text-center">
        <p className="text-muted-foreground">Run a scenario or open an incident to generate a sample evidence record.</p>
      </div>
    );
  }

  const timeline = buildTimeline(incident);
  const now = new Date().toISOString();

  const fields = [
    ["Demonstration record reference", incident.id],
    ["Interaction type", incident.interactionType],
    ["Channel", incident.channel],
    ["Date and time", shortTime(incident.createdAt)],
    ["Risk identified", incident.risk],
    ["Severity", incident.severity],
    ["Policy", `${incident.policy} (${incident.policyVersion})`],
    ["Detection explanation", incident.detection],
    ["Initial system action", incident.action],
    ["Escalation route", incident.escalationRoute],
    ["Human reviewer decision", incident.reviewerDecision || "Pending"],
    ["Reviewer notes", incident.reviewerNotes || "\u2014"],
    ["Final outcome", incident.status],
    ["Record generated", shortTime(now)],
  ];

  const downloadPdf = async () => {
    const { jsPDF } = await import("jspdf");
    const doc = new jsPDF({ unit: "pt", format: "a4" });
    const pw = doc.internal.pageSize.getWidth();
    doc.setFillColor(7, 11, 22);
    doc.rect(0, 0, pw, 60, "F");
    doc.setTextColor(255, 255, 255);
    doc.setFontSize(16);
    doc.text("norse3 \u2014 sample evidence record", 40, 38);
    // watermark
    doc.setTextColor(180, 60, 60);
    doc.setFontSize(11);
    doc.text(EVIDENCE_WATERMARK, 40, 80, { maxWidth: pw - 80 });
    doc.setTextColor(20, 20, 20);
    let y = 110;
    doc.setFontSize(10);
    fields.forEach(([k, v]) => {
      doc.setFont(undefined, "bold");
      doc.text(String(k), 40, y);
      doc.setFont(undefined, "normal");
      const lines = doc.splitTextToSize(String(v), pw - 220);
      doc.text(lines, 220, y);
      y += Math.max(16, lines.length * 14);
      if (y > 760) { doc.addPage(); y = 60; }
    });
    y += 8;
    doc.setFont(undefined, "bold");
    doc.text("Event timeline", 40, y); y += 16;
    doc.setFont(undefined, "normal");
    timeline.forEach((t) => {
      const line = `${t.stage}: ${t.label}`;
      const lines = doc.splitTextToSize(line, pw - 80);
      doc.text(lines, 40, y);
      y += lines.length * 14 + 2;
      if (y > 760) { doc.addPage(); y = 60; }
    });
    doc.save(`norse3-sample-evidence-${incident.id}.pdf`);
  };

  const printRecord = () => window.print();

  return (
    <div>
      <div id="evidence-print" className="relative rounded-lg border border-white/10 bg-navy-900/70 overflow-hidden">
        {/* watermark overlay */}
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden" aria-hidden="true">
          <span className="rotate-[-24deg] text-signal-red/10 font-heading font-bold text-2xl sm:text-4xl text-center leading-tight px-6">
            {EVIDENCE_WATERMARK}
          </span>
        </div>

        <div className="relative p-6 sm:p-8">
          <div className="flex items-center justify-between gap-3 border-b border-white/10 pb-4">
            <div>
              <p className="text-xs uppercase tracking-[0.16em] text-primary">Sample evidence record</p>
              <p className="mt-1 font-heading text-xl font-bold text-foreground">{incident.id}</p>
            </div>
            <CalendarCheck className="h-6 w-6 text-primary" />
          </div>

          <dl className="mt-5 grid gap-x-8 gap-y-3 sm:grid-cols-2">
            {fields.map(([k, v]) => (
              <div key={k} className="flex flex-col">
                <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
                <dd className="mt-0.5 text-sm text-foreground">{v}</dd>
              </div>
            ))}
          </dl>

          <div className="mt-6">
            <p className="text-xs uppercase tracking-wide text-muted-foreground">Complete event timeline</p>
            <ol className="mt-3 space-y-2.5">
              {timeline.map((t) => (
                <li key={t.stage} className="flex items-start gap-3">
                  <span className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                  <div>
                    <span className="text-sm font-medium text-foreground">{t.stage}</span>
                    <span className="ml-2 text-xs text-muted-foreground">{shortTime(t.time)}</span>
                    <p className="text-sm text-muted-foreground">{t.label}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </div>

      <div className="mt-5 flex flex-wrap gap-3 no-print">
        <Button onClick={downloadPdf} className="bg-primary text-primary-foreground hover:bg-primary/90">
          <Download className="mr-1.5 h-4 w-4" /> Download sample PDF
        </Button>
        <Button variant="outline" onClick={printRecord} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
          <Printer className="mr-1.5 h-4 w-4" /> Print record
        </Button>
        {onBack && (
          <Button variant="outline" onClick={onBack} className="border-white/15 bg-transparent hover:bg-white/5 text-foreground">
            <ArrowLeft className="mr-1.5 h-4 w-4" /> Return to incident
          </Button>
        )}
        <Button asChild variant="outline" className="border-primary/40 bg-transparent hover:bg-primary/10 text-primary">
          <Link to="/book-a-demo">Book a walkthrough</Link>
        </Button>
      </div>
    </div>
  );
}
